// ============================================
// src/hooks/usePushNotifications.js
// ============================================
import { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../../api/config';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

function waitForActivation(registration) {
  return new Promise((resolve) => {
    if (registration.active) return resolve();
    const sw = registration.installing || registration.waiting;
    if (!sw) return resolve();
    function onStateChange() {
      if (sw.state === 'activated') {
        sw.removeEventListener('statechange', onStateChange);
        resolve();
      }
    }
    sw.addEventListener('statechange', onStateChange);
  });
}

export const usePushNotifications = (token) => {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const supported =
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      window.isSecureContext;
    setIsSupported(supported);
    if (supported && token) checkSubscriptionStatus();
  }, [token]); // eslint-disable-line

  const checkSubscriptionStatus = async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setIsSubscribed(!!sub);
    } catch {
      setIsSubscribed(false);
    }
  };

  const subscribe = useCallback(async () => {
    if (!isSupported || !token) return;
    setError(null);
    setIsLoading(true);

    try {
      // ── STEP 1: Verify the SW file is actually JS, not HTML ──────────────
      console.log('[Push] Step 1: Checking service-worker.js is served correctly...');
      const swCheck = await fetch('/service-worker.js');
      const contentType = swCheck.headers.get('content-type') || '';
      console.log('[Push] service-worker.js → status:', swCheck.status, '| content-type:', contentType);

      if (contentType.includes('text/html')) {
        throw new Error(
          'service-worker.js is being served as HTML by Netlify. ' +
          'Fix your public/_redirects file — add this line AT THE TOP:\n' +
          '/service-worker.js  /service-worker.js  200'
        );
      }

      // ── STEP 2: Register SW ───────────────────────────────────────────────
      console.log('[Push] Step 2: Registering service worker...');
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      console.log('[Push] SW registered. State:', registration.active?.state ?? 'not yet active');

      // ── STEP 3: Wait for SW to fully activate ────────────────────────────
      console.log('[Push] Step 3: Waiting for SW activation...');
      await waitForActivation(registration);
      console.log('[Push] SW is active ✅');

      // ── STEP 4: Get ready registration ───────────────────────────────────
      const reg = await navigator.serviceWorker.ready;
      console.log('[Push] Step 4: navigator.serviceWorker.ready resolved ✅');

      // ── STEP 5: Permission ────────────────────────────────────────────────
      console.log('[Push] Step 5: Requesting notification permission...');
      const permission = await Notification.requestPermission();
      console.log('[Push] Permission:', permission);
      if (permission !== 'granted') {
        throw new Error('Notification permission denied. Please allow notifications in your browser settings.');
      }

      // ── STEP 6: Fetch VAPID key ───────────────────────────────────────────
      console.log('[Push] Step 6: Fetching VAPID public key...');
      const keyRes = await fetch(`${API_URL}/push/vapid-public-key`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('[Push] VAPID key response status:', keyRes.status);
      if (!keyRes.ok) throw new Error(`Failed to get VAPID key from server (${keyRes.status})`);
      const { publicKey } = await keyRes.json();
      console.log('[Push] VAPID key received ✅');

      // ── STEP 7: Subscribe ─────────────────────────────────────────────────
      console.log('[Push] Step 7: Calling pushManager.subscribe()...');
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
      console.log('[Push] pushManager.subscribe() success ✅');

      // ── STEP 8: Save to backend ───────────────────────────────────────────
      console.log('[Push] Step 8: Saving subscription to backend...');
      const subRes = await fetch(`${API_URL}/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(sub.toJSON()),
      });
      console.log('[Push] Save subscription response:', subRes.status);
      if (!subRes.ok) throw new Error(`Failed to save subscription (${subRes.status})`);

      setIsSubscribed(true);
      console.log('[Push] ✅ Push notifications fully enabled!');

    } catch (err) {
      setError(err.message);
      console.error('[Push] ❌ Subscribe failed at:', err.message, err);
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, token]);

  const unsubscribe = useCallback(async () => {
    if (!isSupported || !token) return;
    setIsLoading(true);
    setError(null);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await fetch(`${API_URL}/push/unsubscribe`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ endpoint: sub.endpoint }),
        });
        await sub.unsubscribe();
      }
      setIsSubscribed(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, token]);

  const sendTest = useCallback(async () => {
    if (!token) return;
    const res = await fetch(`${API_URL}/push/test`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.json();
  }, [token]);

  return { isSupported, isSubscribed, isLoading, error, subscribe, unsubscribe, sendTest };
};
